import { z } from 'zod/v4';

import {
    buttonHorizontalPositionSchema,
    buttonSizeSchema,
    buttonStyleTypeSchema,
    buttonWidthTypeSchema,
    themeButtonDataPartialSchemaLegacy,
} from '@sites/entity-core';

import { FORM_ELEMENT_TYPES } from './form-element-templates';

/**
 * The AI-facing surface the subscription form and the payment form SHARE — declared once because
 * they share it in the persisted data too, not by coincidence.
 *
 * `payment-form/schemas/legacy/data.ts` imports `formElementSchema`, `formInputSettingsSchema`,
 * `formButtonSettingsSchema` and `formAdditionalSettingsSchema` from the form widget's own schemas
 * (its comment says why: the two drive the same `sp-form-*` editor components). So a second copy of
 * this shape, or of the fold that splits it, would be two descriptions of one thing — the exact
 * setup that let indent's schemas drift apart silently.
 *
 * What each type adds on top is small and genuinely its own: the form has `modalTitle` and its own
 * `formView` enum; the payment form has the product, the price and `modalTextHint`.
 */

/** Corner rounding in px, for both the inputs and the modal. Absurdity guard only, as elsewhere. */
const RADIUS_MIN = 0;
const RADIUS_MAX = 64;

/**
 * Width as a percentage of the column — text's exact policy and bounds, for the same reason: the
 * position grid's one meaningful knob, flattened, and capped where the editor's own panel caps it.
 */
const WIDTH_MIN = 1;
const WIDTH_MAX = 100;

/**
 * One field of the form, as the AI tools accept it — three of the element's ~20 persisted keys.
 *
 * Everything else is either derived from `type` (`htmlInputType`, `autocompleteType`, the system
 * `variable`) and owned by `form-element-templates.ts`, or an external id no tool may invent
 * (`crmContactVariable`, `crmDealVariable`, the six messenger variable bags). This is the same
 * decision video made about its derived fields, and for the same reason: a model that can name
 * `htmlInputType` can pair `type: "date"` with `htmlInputType: "checkbox"`, and the result validates
 * against the element schema while rendering a control the editor would never produce.
 */
const partialFormFieldSchema = z
    .object({
        type: z
            .enum(FORM_ELEMENT_TYPES)
            .describe(
                'What the field collects. "email" is the one field every form of this kind needs — it is ' +
                    'added automatically if you leave it out, and it cannot be deleted.'
            ),
        label: z
            .string()
            .optional()
            .describe('Visible label, in the language of the page. Omit only for a field that needs none.'),
        placeholder: z.string().optional().describe('Greyed-out hint inside the input.'),
        required: z
            .boolean()
            .optional()
            .describe(
                'Whether the visitor must fill it in. Ignored for email and phone, which are always ' +
                    'required and cannot be removed.'
            ),
        options: z
            .array(z.string())
            .optional()
            .describe('The choices, for "radio" and "select" only. REQUIRED for those two — see below.'),
    })
    /**
     * A choice field with no choices is the silent-nonsense outcome this widget is prone to: the
     * template seeds `options: []`, `buildFormElement` only fills it when the caller supplied one,
     * and nothing refused the gap — so a `select` created with just a label renders a labelled
     * dropdown a visitor cannot pick anything from, reported as a successful create.
     *
     * Raised as a `custom` issue on purpose: the forgiving validator repairs or drops what it can,
     * and `custom` is the one code it will not answer by deleting something — it surfaces the message
     * instead. Deleting here would mean building the form WITHOUT the field that was asked for.
     */
    .superRefine((field, ctx) => {
        if (field.type !== 'radio' && field.type !== 'select') {
            return;
        }

        if (!field.options?.length) {
            ctx.addIssue({
                code: 'custom',
                path: ['options'],
                message:
                    `a "${field.type}" field needs its choices: send options as a list of strings, ` +
                    'e.g. ["Базовый", "Про"]. Without them the visitor sees a control with nothing to pick.',
            });
        }
    });

export type PartialFormField = z.infer<typeof partialFormFieldSchema>;

/**
 * The shared shape, as a plain object of zod fields so each widget's own schema can spread it and
 * add its extras. Not a `z.object()` to `.extend()`, because both users need to interleave their own
 * fields into the same flat level and `.extend()` would put them after — cosmetic, but the field
 * order is what a model reads first in the JSON Schema.
 */
export const formAppearanceShape = {
    fields: z
        .array(partialFormFieldSchema)
        .min(1)
        .optional()
        .describe(
            'The fields to show, in order. REPLACES the whole list — include every field you want to ' +
                'keep, not just the new one. An email field is added if your list has none, and is always ' +
                'moved first.'
        ),

    // --- submit button ---
    buttonLabel: z.string().optional().describe('Text on the submit button.'),
    buttonStyle: buttonStyleTypeSchema
        .optional()
        .describe(
            'Which site button preset the submit button uses: "primary" (default), "secondary", ' +
                '"classic", or "custom" to take its colours from buttonBase.'
        ),
    buttonSize: buttonSizeSchema.optional().describe('Submit button size: "S", "M" or "L".'),
    buttonWidth: buttonWidthTypeSchema.optional().describe('Submit button width: "full", "auto" or "fixed".'),
    buttonBase: themeButtonDataPartialSchemaLegacy
        .optional()
        .describe(
            'Custom colours/border for the submit button — partial, only the keys you provide change, ' +
                'e.g. { "background": "primary", "color": "#ffffff" }. Supplying this switches buttonStyle ' +
                'to "custom" on its own, without which the site preset keeps winning and nothing changes.'
        ),

    // --- inputs ---
    formStyle: z
        .enum(['vertical', 'horizontal'])
        .optional()
        .describe('Field layout: "vertical" (default, one per row) or "horizontal" (inputs and button in a row).'),
    inputSize: buttonSizeSchema.optional().describe('Input height: "S", "M" or "L" (default).'),
    showLabels: z.boolean().optional().describe('Whether field labels are shown above the inputs.'),
    inputBorderType: z
        .enum(['fullBorder', 'bottomBorder'])
        .optional()
        .describe('Input border: "fullBorder" (default) or "bottomBorder" (an underline only).'),
    borderRadius: z.number().int().min(RADIUS_MIN).max(RADIUS_MAX).optional().describe('Input corner rounding in px.'),
    backgroundColor: z.string().optional().describe('Input fill colour.'),
    borderColor: z.string().optional().describe('Input border colour.'),
    textColor: z.string().optional().describe('Label and helper-text colour.'),
    inputTextColor: z.string().optional().describe('Colour of what the visitor types.'),
    horizontalAlign: buttonHorizontalPositionSchema
        .optional()
        .describe('Form alignment inside the column: "L" (left), "M" (center) or "R" (right).'),

    // --- after submit ---
    successMessage: z
        .string()
        .optional()
        .describe(
            'What the visitor sees after a successful submit. HTML is allowed. Setting this switches ' +
                'the post-submit action to "show a message".'
        ),
    successLink: z
        .string()
        .optional()
        .describe(
            'Where the visitor is sent after a successful submit, as a full URL. Setting this switches ' +
                'the post-submit action to "open a link" — send one or the other, not both.'
        ),

    // --- widget envelope ---
    width: z
        .number()
        .int()
        .min(WIDTH_MIN)
        .max(WIDTH_MAX)
        .optional()
        .describe(
            'Form width inside its column, as a percent — what the editor’s position panel offers for a ' +
                'normally placed form. A form free-placed in a zero block stores its width in PIXELS, so ' +
                'this knob is not the way to resize one of those; leave it alone there.'
        ),
    hideOnDesktop: z.boolean().optional().describe('Hide this widget on desktop screens.'),
    hideOnTablet: z.boolean().optional().describe('Hide this widget on tablet screens.'),
    hideOnMobile: z.boolean().optional().describe('Hide this widget on mobile screens.'),
} as const;

/**
 * Which persisted group each flat key belongs to. The fold reads these maps; the shape above declares
 * the keys. Exported from the same module so a key added to one is visibly missing from the other —
 * image's split lists (`IMAGE_SOURCE_KEYS`/`IMAGE_STYLE_KEYS`) exist for the same reason, having been
 * the fix for a patch landing on the wrong level.
 *
 * Renaming is not cosmetic: `formStyle` and `horizontalAlign` are the names a model reliably reaches
 * for, while the entity calls them `formStyleType` and `horizontalPosition`.
 */
export const FORM_INPUT_SETTINGS_KEY_MAP: Readonly<Record<string, string>> = {
    formStyle: 'formStyleType',
    inputSize: 'inputSize',
    showLabels: 'showLabels',
    inputBorderType: 'inputBorderType',
    borderRadius: 'borderRadius',
    backgroundColor: 'backgroundColor',
    borderColor: 'borderColor',
    textColor: 'textColor',
    inputTextColor: 'inputTextColor',
    horizontalAlign: 'horizontalPosition',
};

export const FORM_BUTTON_SETTINGS_KEY_MAP: Readonly<Record<string, string>> = {
    buttonLabel: 'label',
    buttonStyle: 'styleType',
    buttonSize: 'size',
    buttonWidth: 'widthType',
};

/** Visibility keys, shared and named the same on `data` itself. */
export const FORM_VISIBILITY_KEYS: readonly string[] = ['hideOnDesktop', 'hideOnTablet', 'hideOnMobile'];
